export default class MeshData {
  vertices: Uint8Array;
  indices: Uint16Array;
  stride_bytes: number;
  skinned: boolean;

  constructor(vertices: Uint8Array, indices: Uint16Array, stride_bytes: number, skinned: boolean) 
  {
    this.vertices = vertices;
    this.indices = indices;
    this.stride_bytes = stride_bytes;
    this.skinned = skinned;
  }

  static make_cube(): MeshData {
    const vertices = new Float32Array([
      // front (+Z)
      -0.5, -0.5,  0.5,   0.0,  0.0,  1.0,   0.0, 1.0,
       0.5, -0.5,  0.5,   0.0,  0.0,  1.0,   1.0, 1.0,
       0.5,  0.5,  0.5,   0.0,  0.0,  1.0,   1.0, 0.0,
      -0.5,  0.5,  0.5,   0.0,  0.0,  1.0,   0.0, 0.0,

      // back (-Z)
       0.5, -0.5, -0.5,   0.0,  0.0, -1.0,   0.0, 1.0,
      -0.5, -0.5, -0.5,   0.0,  0.0, -1.0,   1.0, 1.0,
      -0.5,  0.5, -0.5,   0.0,  0.0, -1.0,   1.0, 0.0,
       0.5,  0.5, -0.5,   0.0,  0.0, -1.0,   0.0, 0.0,

      // left (-X)
      -0.5, -0.5, -0.5,  -1.0,  0.0,  0.0,   0.0, 1.0,
      -0.5, -0.5,  0.5,  -1.0,  0.0,  0.0,   1.0, 1.0,
      -0.5,  0.5,  0.5,  -1.0,  0.0,  0.0,   1.0, 0.0,
      -0.5,  0.5, -0.5,  -1.0,  0.0,  0.0,   0.0, 0.0,

      // right (+X)
       0.5, -0.5,  0.5,   1.0,  0.0,  0.0,   0.0, 1.0,
       0.5, -0.5, -0.5,   1.0,  0.0,  0.0,   1.0, 1.0,
       0.5,  0.5, -0.5,   1.0,  0.0,  0.0,   1.0, 0.0,
       0.5,  0.5,  0.5,   1.0,  0.0,  0.0,   0.0, 0.0,

      // top (+Y)
      -0.5,  0.5,  0.5,   0.0,  1.0,  0.0,   0.0, 1.0,
       0.5,  0.5,  0.5,   0.0,  1.0,  0.0,   1.0, 1.0,
       0.5,  0.5, -0.5,   0.0,  1.0,  0.0,   1.0, 0.0,
      -0.5,  0.5, -0.5,   0.0,  1.0,  0.0,   0.0, 0.0,

      // bottom (-Y)
      -0.5, -0.5, -0.5,   0.0, -1.0,  0.0,   0.0, 1.0,
       0.5, -0.5, -0.5,   0.0, -1.0,  0.0,   1.0, 1.0,
       0.5, -0.5,  0.5,   0.0, -1.0,  0.0,   1.0, 0.0,
      -0.5, -0.5,  0.5,   0.0, -1.0,  0.0,   0.0, 0.0,
    ]);

    const indices = new Uint16Array([
       0,  1,  2,   0,  2,  3,
       4,  5,  6,   4,  6,  7,
       8,  9, 10,   8, 10, 11,
      12, 13, 14,  12, 14, 15,
      16, 17, 18,  16, 18, 19,
      20, 21, 22,  20, 22, 23,
    ]);

      return new MeshData(new Uint8Array(vertices.buffer.slice(0)), indices, 32, false);
  }

  static make_plane(width: number = 1, height: number = 1): MeshData {
  const hw = width * 0.5;
  const hh = height * 0.5;

  const vertices = new Float32Array([
    -hw, 0.0, -hh,   0.0, 1.0, 0.0,   0.0, 1.0,
     hw, 0.0, -hh,   0.0, 1.0, 0.0,   1.0, 1.0,
     hw, 0.0,  hh,   0.0, 1.0, 0.0,   1.0, 0.0,
    -hw, 0.0,  hh,   0.0, 1.0, 0.0,   0.0, 0.0,
  ]);

  const indices = new Uint16Array([
    0, 1, 2,
    0, 2, 3,
  ]);

   return new MeshData(new Uint8Array(vertices.buffer.slice(0)), indices, 32, false);
}

static make_pivot_offset_ground_plane(
  width: number = 1,
  depth: number = 1,
  pivot_offset_v: number = 0.15
): MeshData {
  const hw = width * 0.5;
  const hd = depth * 0.5;

  // pivot_offset_v is in normalized UV space (0..1).
  // 0.0   = pivot at geometric center (no shift)
  // > 0   = shifts the mesh so the texture's upper region
  //         (where the bullseye sits) is centered on the pivot
  const shift = pivot_offset_v * depth;

  const z0 = -hd - shift; // far edge (top of texture)
  const z1 =  hd - shift; // near edge (bottom of texture)

  const vertices = new Float32Array([
    // x,    y,    z,     nx,  ny,  nz,    u,   v
    -hw,   0.0,  z0,    0.0, 1.0, 0.0,   0.0, 0.0,
     hw,   0.0,  z0,    0.0, 1.0, 0.0,   1.0, 0.0,
     hw,   0.0,  z1,    0.0, 1.0, 0.0,   1.0, 1.0,
    -hw,   0.0,  z1,    0.0, 1.0, 0.0,   0.0, 1.0,
  ]);

  const indices = new Uint16Array([
    0, 1, 2,
    0, 2, 3,
  ]);

  return new MeshData(new Uint8Array(vertices.buffer.slice(0)), indices, 32, false);
}

static make_bottom_anchored_vertical_plane(
  width: number = 1,
  height: number = 1
): MeshData {
  const hw = width * 0.5;

  const vertices = new Float32Array([
    -hw, 0.0,    0.0,   0.0, 0.0, 1.0,   0.0, 1.0,
     hw, 0.0,    0.0,   0.0, 0.0, 1.0,   1.0, 1.0,
     hw, height, 0.0,   0.0, 0.0, 1.0,   1.0, 0.0,
    -hw, height, 0.0,   0.0, 0.0, 1.0,   0.0, 0.0,
  ]);

  const indices = new Uint16Array([
    0, 1, 2,
    0, 2, 3,
  ]);

  return new MeshData(new Uint8Array(vertices.buffer.slice(0)), indices, 32, false);
}

  static make_sphere(
    radius: number = 0.5,
    latBands: number = 16,
    lonBands: number = 16
  ): MeshData {
    const verts: number[] = [];
    const inds: number[] = [];

    for (let lat = 0; lat <= latBands; lat++) {
      const v = lat / latBands;
      const theta = v * Math.PI;
      const sinTheta = Math.sin(theta);
      const cosTheta = Math.cos(theta);

      for (let lon = 0; lon <= lonBands; lon++) {
        const u = lon / lonBands;
        const phi = u * Math.PI * 2.0;
        const sinPhi = Math.sin(phi);
        const cosPhi = Math.cos(phi);

        const nx = cosPhi * sinTheta;
        const ny = cosTheta;
        const nz = sinPhi * sinTheta;

        const x = nx * radius;
        const y = ny * radius;
        const z = nz * radius;

        verts.push(
          x, y, z,
          nx, ny, nz,
          u, 1.0 - v
        );
      }
    }

    for (let lat = 0; lat < latBands; lat++) {
      for (let lon = 0; lon < lonBands; lon++) {
        const first = lat * (lonBands + 1) + lon;
        const second = first + lonBands + 1;

        inds.push(first, second, first + 1);
        inds.push(second, second + 1, first + 1);
      }
    }

    
    const vertices = new Float32Array(verts);
    const indices = new Uint16Array(inds);

    return new MeshData(
      new Uint8Array(vertices.buffer.slice(0)),
      indices,
      32,
      false
    );
  }
}